export interface IAiReviewCriteriaItem {
	name: string;
	result: string;
	reason: string;
	weight?: number;
	equals?: unknown;
	changes?: unknown;
}

export interface IAiReviewServiceItem {
	section?: string;
	name: string;
	sku?: string;
	value?: string;
	status: string;
	reason: string;
}

export interface IAiReviewSummary {
	criteria?: IAiReviewCriteriaItem[];
	services?: IAiReviewServiceItem[];
	criteriaEvaluated?: boolean;
	criteriaMessage?: string;
}

export interface IAiResultFields {
	aiCheckEnabled?: boolean;
	taskRuleVisible?: boolean;
	requiresPhoto?: boolean;
	aiResult?: string;
	aiComment?: string;
	aiStatus?: string;
	aiReason?: string;
	status?: string;
	result?: string;
	reason?: string;
	comment?: string;
	aiCheckResult?: string;
	aiCheckComment?: string;
	aiCheckStatus?: string;
	aiCheckReason?: string;
	aiReviewResult?: string;
	aiReviewComment?: string;
	ai?: {
		result?: string;
		status?: string;
		comment?: string;
		reason?: string;
	};
}

export interface INormalizedAiResult {
	result: string;
	comment: string;
}

const pickFirstString = (...values: Array<string | undefined>) =>
	values.find((value) => typeof value === 'string' && value.trim().length > 0) || '';

export const normalizeAiResult = (
	item?: IAiResultFields | INormalizedAiResult | null
): INormalizedAiResult | null => {
	if (!item) {
		return null;
	}

	const fields = item as IAiResultFields;

	const rawResult = pickFirstString(
		fields.result,
		fields.status,
		fields.aiCheckResult,
		fields.aiCheckStatus,
		fields.aiReviewResult,
		fields.aiResult,
		fields.aiStatus,
		fields.ai?.result,
		fields.ai?.status
	);
	const statusLabels: Record<string, string> = {
		done: 'Выполнено',
		completed: 'Выполнено',
		success: 'Выполнено',
		passed: 'Выполнено',
		pass: 'Выполнено',
		failed: 'Не выполнено',
		fail: 'Не выполнено',
		rejected: 'Не выполнено',
		declined: 'Не выполнено',
		unconfirmed: 'Не подтверждено',
		uncertain: 'Не определено',
	};
	const result =
		statusLabels[rawResult.trim().toLowerCase()] || rawResult;
	const comment = pickFirstString(
		fields.reason,
		fields.comment,
		fields.aiCheckComment,
		fields.aiCheckReason,
		fields.aiReviewComment,
		fields.aiComment,
		fields.aiReason,
		fields.ai?.comment,
		fields.ai?.reason
	);

	if (!result && !comment) {
		return null;
	}

	return { result, comment };
};

export const findAiServiceResult = (
	services: IAiReviewServiceItem[] | undefined,
	params: { name?: string; sku?: string; section?: string },
	fallback?: IAiResultFields | null
): INormalizedAiResult | null => {
	const fromFallback = normalizeAiResult(fallback);
	if (!services?.length || !params.name) {
		return fromFallback;
	}

	const name = params.name.trim().toLowerCase();
	const sku = params.sku?.trim().toLowerCase();
	const sectionAliases = normalizeSection(params.section);

	const byExact =
		services.find((service) => {
			const serviceName = service.name?.trim().toLowerCase() || '';
			const serviceSku = service.sku?.trim().toLowerCase() || '';
			const serviceSection = normalizeSection(service.section);

			const nameOk = serviceName === name;
			const skuOk =
				!sku ||
				!serviceSku ||
				serviceSku === sku ||
				serviceSku.includes(sku) ||
				sku.includes(serviceSku);
			const sectionOk =
				!sectionAliases.length ||
				!serviceSection.length ||
				sectionAliases.some((alias) => serviceSection.includes(alias));

			return nameOk && skuOk && sectionOk;
		}) ||
		services.find((service) => {
			const serviceName = service.name?.trim().toLowerCase() || '';
			return serviceName === name;
		}) ||
		services.find((service) => {
			const serviceName = service.name?.trim().toLowerCase() || '';
			return (
				serviceName.includes(name) ||
				name.includes(serviceName)
			);
		});

	if (!byExact) {
		return fromFallback;
	}

	return (
		normalizeAiResult({
			status: byExact.status,
			result: byExact.status,
			reason: byExact.reason,
			comment: byExact.reason,
		}) || fromFallback
	);
};

const normalizeSection = (section?: string): string[] => {
	if (!section?.trim()) {
		return [];
	}

	const raw = section
		.trim()
		.toLowerCase()
		.replace(/[_\s.-]+/g, '');
	const aliases: Record<string, string[]> = {
		cv: ['cv', 'tt', 'данныепотт', 'datapott', 'доп', 'допуслуга', 'допуслуг'],
		skucv: ['skucv', 'доп', 'допуслуга', 'допуслуг', 'additionalservices'],
		допуслуга: ['cv', 'skucv', 'доп', 'допуслуга', 'допуслуг'],
		actions: ['actions', 'actionstt', 'акциипотт', 'actionsbytt'],
		actionstt: ['actions', 'actionstt', 'акциипотт', 'actionsbytt'],
		skuactions: ['skuactions', 'actions', 'акциипотоварам'],
	};

	return aliases[raw] || [raw];
};

export const shouldShowAiResults = (
	isCustomer?: boolean,
	aiReview?: boolean,
	_claims?: string[] | null
): boolean => {
	if (isCustomer === true) {
		return aiReview === true;
	}
	return true;
};

export type AiFoundPriceItem = {
	price: number;
	photoIndex: number;
	photoLabel: number;
	mediaId?: string;
};

type AiFoundPriceSkuRef = {
	id?: number | string | null;
	name?: string | null;
};

const normalizeSkuName = (value?: string | null): string =>
	(value || '')
		.toLowerCase()
		.replace(/ё/g, 'е')
		.replace(/[^a-zа-я0-9]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const skuIdsEqual = (left?: number | string | null, right?: number | string | null): boolean =>
	left != null && right != null && String(left) === String(right);

const skuNamesMatch = (left?: string | null, right?: string | null): boolean => {
	const a = normalizeSkuName(left);
	const b = normalizeSkuName(right);
	if (!a || !b) {
		return false;
	}
	return a === b || a.includes(b) || b.includes(a);
};

const candidateMatchesSku = (
	sku: AiFoundPriceSkuRef,
	candidate: { skuId?: number | string; id?: number | string; sku?: { id?: number | string }; name?: string }
): boolean => {
	const candidateId = candidate.skuId ?? candidate.id ?? candidate.sku?.id;
	if (skuIdsEqual(sku.id, candidateId)) {
		return true;
	}
	return skuNamesMatch(sku.name, candidate.name);
};

const toFoundPrice = (
	price: unknown,
	mediaId: number | string | undefined | null,
	medias: Array<{ media?: { id?: number | string } }>
): AiFoundPriceItem | null => {
	if (typeof price !== 'number' || Number.isNaN(price)) {
		return null;
	}
	const id = mediaId != null ? String(mediaId) : '';
	let photoIndex = id
		? medias.findIndex((media) => String(media?.media?.id) === id)
		: -1;
	if (photoIndex < 0 && id) {
		photoIndex = medias.findIndex((media) => String((media as any)?.mediaId) === id);
	}
	return {
		price,
		photoIndex,
		photoLabel: photoIndex >= 0 ? photoIndex + 1 : 0,
		mediaId: id || undefined,
	};
};

export const formatFoundPrice = (price: number | null | undefined): string => {
	if (price == null || Number.isNaN(Number(price))) {
		return '';
	}
	return Number.isInteger(price) ? String(price) : Number(price).toFixed(2);
};

export const getAiFoundPrices = (
	aiReview: any,
	sku: AiFoundPriceSkuRef,
	medias: Array<{ media?: { id?: number | string } }> = []
): AiFoundPriceItem[] => {
	if (!aiReview || (sku.id == null && !sku.name)) {
		return [];
	}

	const photos = Array.isArray(aiReview.photos) ? aiReview.photos : [];
	const gallery =
		medias.length > 0
			? medias
			: photos.map((photo: any) => ({
					media: { id: photo?.mediaId ?? photo?.media?.id },
					mediaId: photo?.mediaId ?? photo?.media?.id,
			  }));
	const reviewSkus = Array.isArray(aiReview.skus) ? aiReview.skus : [];
	const items: AiFoundPriceItem[] = [];

	photos.forEach((photo: any) => {
		const mediaId = photo?.mediaId ?? photo?.media?.id;
		const tags = Array.isArray(photo?.pricetags) ? photo.pricetags : [];
		tags.forEach((tag: any) => {
			const linkedSku = reviewSkus.find((item: any) =>
				candidateMatchesSku(sku, item)
			);
			const matchesTag =
				candidateMatchesSku(sku, tag) ||
				(linkedSku && skuIdsEqual(linkedSku?.skuId ?? linkedSku?.id, tag?.skuId));
			if (!matchesTag) {
				return;
			}
			const found = toFoundPrice(tag.price, mediaId, gallery);
			if (found) {
				items.push(found);
			}
		});
	});

	if (!items.length) {
		reviewSkus.forEach((reviewSku: any) => {
			if (!candidateMatchesSku(sku, reviewSku)) {
				return;
			}
			const appearsIn = Array.isArray(reviewSku.appearsIn)
				? reviewSku.appearsIn
				: [];
			if (appearsIn.length) {
				appearsIn.forEach((entry: any) => {
					const found = toFoundPrice(
						entry?.price ?? reviewSku.price,
						entry?.mediaId,
						gallery
					);
					if (found) {
						items.push(found);
					}
				});
			} else {
				const found = toFoundPrice(
					reviewSku.price,
					reviewSku.mediaId,
					gallery
				);
				if (found) {
					items.push(found);
				}
			}
		});
	}

	const unique = new Map<string, AiFoundPriceItem>();
	items.forEach((item) => {
		const key = `${item.price}-${item.mediaId || item.photoIndex}`;
		if (!unique.has(key)) {
			unique.set(key, item);
		}
	});

	return Array.from(unique.values()).sort(
		(left, right) => left.photoIndex - right.photoIndex || left.price - right.price
	);
};

export const getAiFoundPrice = (
	aiReview: any,
	skuId?: number | string | null,
	skuName?: string | null,
	medias?: Array<{ media?: { id?: number | string } }>
): number | null =>
	getAiFoundPrices(aiReview, { id: skuId, name: skuName }, medias)[0]?.price ?? null;

export const hasAiFoundPriceData = (aiReview: any): boolean => {
	if (!aiReview) {
		return false;
	}
	if (
		Array.isArray(aiReview.skus) &&
		aiReview.skus.some(
			(sku: any) => typeof sku?.price === 'number' && !Number.isNaN(sku.price)
		)
	) {
		return true;
	}
	return (
		Array.isArray(aiReview.photos) &&
		aiReview.photos.some((photo: any) =>
			(photo?.pricetags || []).some(
				(tag: any) => typeof tag?.price === 'number' && !Number.isNaN(tag.price)
			)
		)
	);
};

export const hasAiFoundPrices = (
	aiReview: any,
	skuIds: Array<number | string | undefined>
): boolean =>
	hasAiFoundPriceData(aiReview) ||
	skuIds.some((skuId) => getAiFoundPrice(aiReview, skuId) != null);

export const findAiPhotoResult = (
	aiReview: any,
	media: {
		media?: { id?: number | string };
		aiResult?: string;
		aiComment?: string;
		aiStatus?: string;
		aiReason?: string;
		status?: string;
		result?: string;
		reason?: string;
		comment?: string;
		recognition?: IAiResultFields;
		ai?: IAiResultFields['ai'];
	}
): INormalizedAiResult | null => {
	const fromMedia = normalizeAiResult(media) || normalizeAiResult(media.recognition);
	if (fromMedia) {
		return fromMedia;
	}

	const mediaId = media.media?.id;
	if (mediaId == null || !aiReview) {
		return null;
	}

	const photoLists = [
		aiReview.photos,
		aiReview.medias,
		aiReview.photoResults,
		aiReview.recognitions,
		aiReview.mediaResults,
	].filter(Array.isArray);

	for (const list of photoLists) {
		const matched = list.find((item: any) => {
			const id =
				item?.mediaId ??
				item?.media?.id ??
				item?.id ??
				item?.photoId;
			return String(id) === String(mediaId);
		});
		if (matched) {
			const fromFields =
				normalizeAiResult(matched) || normalizeAiResult(matched.ai);
			if (fromFields) {
				return fromFields;
			}

			const boxes = Array.isArray(matched.boxes) ? matched.boxes : [];
			if (boxes.length > 0) {
				const names = Array.from(
					new Set(
						boxes
							.map((box: { name?: string }) => box?.name)
							.filter(Boolean)
					)
				) as string[];
				return {
					result: `Распознано (${boxes.length})`,
					comment:
						names.slice(0, 6).join(', ') +
						(names.length > 6 ? '…' : ''),
				};
			}

			if (matched.taskName && boxes.length === 0) {
				return null;
			}
		}
	}

	if (Array.isArray(aiReview.skus)) {
		const onPhoto = aiReview.skus.filter((sku: any) =>
			(sku?.appearsIn || []).some(
				(item: { mediaId?: number | string }) =>
					String(item.mediaId) === String(mediaId)
			)
		);
		if (onPhoto.length) {
			return {
				result: `Распознано (${onPhoto.length})`,
				comment: onPhoto
					.map((sku: { name?: string; faces?: number }) =>
						sku.faces
							? `${sku.name} — ${sku.faces} фейс.`
							: sku.name
					)
					.filter(Boolean)
					.slice(0, 6)
					.join('; '),
			};
		}
	}

	return null;
};

export const PHOTO_INDEX_REGEX = /\[(\d+)\]/g;
